<template>
    <Card pageTitle="Checkout Policy">
        <AllowPermission :permission="PERMISSION_CODES.CMS_VIEW_CASH_MANAGEMENT">
            <div class="settings-wrap">
                <p class="description">
                    Control whether POS staff must save an order before proceeding to checkout or tender.
                </p>

                <div class="field-row checkbox-row">
                    <input
                        id="require-save-before-checkout"
                        type="checkbox"
                        v-model="requireSaveBeforeCheckout"
                        :disabled="!canEdit || loading || saving || !configLoaded"
                    />
                    <label for="require-save-before-checkout">Require save before checkout</label>
                </div>

                <div class="policy-preview">
                    Current policy:
                    <strong>{{
                        requireSaveBeforeCheckout ? "Save is required before checkout." : "Checkout is allowed without saving."
                    }}</strong>
                </div>

                <h3>Save Order only by location</h3>
                <p class="description">
                    Enable only the locations that should save orders instead of taking payment.
                    Selected checkout terminals can still tender saved or new orders.
                    Other locations keep their current checkout behavior.
                </p>
                <div v-for="location in policyLocations" :key="location.warehouseId" class="location-policy">
                    <label>
                        <input type="checkbox" :checked="isSaveOnlyLocation(location.warehouseId)"
                            :disabled="!canEdit || loading || saving || !configLoaded"
                            @change="setSaveOnlyLocation(location.warehouseId, $event.target.checked)" />
                        {{ location.name }}: Save Order only
                    </label>
                    <div v-if="isSaveOnlyLocation(location.warehouseId)">
                        <label :for="'checkout-terminals-' + location.warehouseId">Checkout terminal exceptions</label>
                        <select multiple :id="'checkout-terminals-' + location.warehouseId"
                            :disabled="!canEdit || loading || saving || !configLoaded"
                            @change="setCheckoutTerminals(location.warehouseId, $event)">
                            <option v-for="terminal in location.checkoutTerminals" :key="terminal.terminalBindingId"
                                :value="terminal.terminalBindingId"
                                :selected="checkoutTerminalIds(location.warehouseId).includes(terminal.terminalBindingId)">{{ terminal.name }}</option>
                        </select>
                        <p class="description">With no exceptions, every terminal at this location saves orders only.</p>
                    </div>
                </div>

                <div class="actions">
                    <Button borderOnly class="me-2" @click="loadConfig" :disabled="loading || saving">
                        Reload
                    </Button>
                    <Button v-if="canEdit" @click="saveConfig" :disabled="loading || saving || !configLoaded">
                        {{ saving ? "Saving..." : "Save" }}
                    </Button>
                </div>
            </div>
        </AllowPermission>
    </Card>
</template>

<script>
import { mapState } from "vuex"
import { checkHasPermission, PERMISSION_CODES } from "../../store/permission"
import Card from "../../components/UI/Card.vue"
import Button from "../../components/UI/Button.vue"
import AllowPermission from "../../components/layout/AllowPermission.vue"
import CashManagementFetchApi from "../../utils/fetch/cash-management-fetch-api"
import { errMsg } from "../../utils/common"

export default {
    components: {
        Card,
        Button,
        AllowPermission,
    },
    data() {
        return {
            PERMISSION_CODES,
            loading: false,
            saving: false,
            requireSaveBeforeCheckout: false,
            configLoaded: false,
            saveOrderOnlyLocations: [],
            availableLocations: [],
        }
    },
    computed: {
        ...mapState("auth", ["user"]),
        policyLocations() {
            const locations = this.availableLocations.map(location => ({ ...location,
                checkoutTerminals: [...(location.checkoutTerminals || [])] }))
            for (const configured of this.saveOrderOnlyLocations) {
                let location = locations.find(row => row.warehouseId === configured.warehouseId)
                if (!location) {
                    location = { warehouseId: configured.warehouseId, name: "Unavailable location", checkoutTerminals: [] }
                    locations.push(location)
                }
                for (const binding of configured.checkoutTerminalBindingIds) {
                    if (!location.checkoutTerminals.some(terminal => terminal.terminalBindingId === binding)) {
                        location.checkoutTerminals.push({ terminalBindingId: binding, name: "Unavailable terminal (remove before saving)" })
                    }
                }
            }
            return locations
        },
        canEdit() {
            return checkHasPermission({
                isAdmin: this.user?.isAdmin,
                permissionId: PERMISSION_CODES.CMS_EDIT_CASH_MANAGEMENT,
                userPermissionIds: this.user?.permissionIds || [],
            })
        },
    },
    async mounted() {
        await this.loadConfig()
    },
    methods: {
        async loadConfig() {
            this.loading = true
            try {
                const config = await CashManagementFetchApi.fetchCheckoutPolicyConfig()
                this.requireSaveBeforeCheckout = !!config?.requireSaveBeforeCheckout
                this.saveOrderOnlyLocations = (config?.saveOrderOnlyLocations || []).map(row => ({ ...row,
                    checkoutTerminalBindingIds: [...row.checkoutTerminalBindingIds] }))
                this.availableLocations = config?.availableLocations || []
                this.configLoaded = true
            } catch (err) {
                this.$toast.error(errMsg(err, "Could not load checkout policy config"))
            }
            this.loading = false
        },
        isSaveOnlyLocation(warehouseId) {
            return this.saveOrderOnlyLocations.some(row => row.warehouseId === warehouseId)
        },
        checkoutTerminalIds(warehouseId) {
            return this.saveOrderOnlyLocations.find(row => row.warehouseId === warehouseId)?.checkoutTerminalBindingIds || []
        },
        setSaveOnlyLocation(warehouseId, enabled) {
            if (!this.canEdit || !this.configLoaded || this.loading || this.saving) return
            this.saveOrderOnlyLocations = this.saveOrderOnlyLocations.filter(row => row.warehouseId !== warehouseId)
            if (enabled) this.saveOrderOnlyLocations.push({ warehouseId, checkoutTerminalBindingIds: [] })
        },
        setCheckoutTerminals(warehouseId, event) {
            if (!this.canEdit || !this.configLoaded || this.loading || this.saving) return
            const location = this.saveOrderOnlyLocations.find(row => row.warehouseId === warehouseId)
            if (location) location.checkoutTerminalBindingIds = Array.from(event.target.selectedOptions).map(option => option.value)
        },
        async saveConfig() {
            if (!this.canEdit || !this.configLoaded || this.loading || this.saving) return
            this.saving = true
            try {
                const updated = await CashManagementFetchApi.updateCheckoutPolicyConfig({
                    requireSaveBeforeCheckout: !!this.requireSaveBeforeCheckout,
                    saveOrderOnlyLocations: this.saveOrderOnlyLocations.map(row => ({ warehouseId: row.warehouseId,
                        checkoutTerminalBindingIds: [...row.checkoutTerminalBindingIds] })),
                })
                this.requireSaveBeforeCheckout = !!updated?.requireSaveBeforeCheckout
                this.saveOrderOnlyLocations = (updated?.saveOrderOnlyLocations || []).map(row => ({ ...row,
                    checkoutTerminalBindingIds: [...row.checkoutTerminalBindingIds] }))
                this.$toast.success("Checkout policy config updated")
            } catch (err) {
                this.$toast.error(errMsg(err, "Could not update checkout policy config"))
            }
            this.saving = false
        },
    },
}
</script>

<style lang="scss" scoped>
.settings-wrap {
    max-width: 640px;
}

.description {
    margin-bottom: 16px;
    color: #5f6368;
}

.field-row {
    margin-bottom: 16px;
}

.checkbox-row {
    display: flex;
    align-items: center;
    gap: 8px;
}

.policy-preview {
    padding: 12px;
    border: 1px solid #e0e0e0;
    border-radius: 8px;
    margin-bottom: 16px;
}

.location-policy {
    padding: 12px;
    margin-bottom: 12px;
    border: 1px solid #e0e0e0;
    border-radius: 8px;
    select { display: block; width: 100%; min-height: 80px; margin: 8px 0; }
}

.actions {
    display: flex;
    align-items: center;
}
</style>
